<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps({
    modelValue: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const fileInput = ref(null)
const errorMessage = ref('')
const isDragging = ref(false)
const maxSize = 2 * 1024 * 1024

const openFileDialog = () => {
    fileInput.value?.click()
}

const clearLogo = () => {
    errorMessage.value = ''
    emit('update:modelValue', '')

    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const handleFile = (file) => {
    errorMessage.value = ''

    if (!file) {
        return
    }

    if (!file.type.startsWith('image/')) {
        errorMessage.value = t('components.authentication.companySetup.logoInvalidType')
        return
    }

    if (file.size > maxSize) {
        errorMessage.value = t('components.authentication.companySetup.logoMaxSize')
        return
    }

    const reader = new FileReader()
    reader.onload = () => emit('update:modelValue', reader.result)
    reader.readAsDataURL(file)
}

const handleInputChange = (event) => {
    handleFile(event.target.files?.[0])
}

const handleDrop = (event) => {
    isDragging.value = false
    handleFile(event.dataTransfer.files?.[0])
}
</script>

<template>
    <div class="logo-upload">
        <button
            type="button"
            :class="['logo-upload__dropzone', { 'logo-upload__dropzone--active': isDragging }]"
            @click="openFileDialog"
            @dragenter.prevent="isDragging = true"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
        >
            <span v-if="!modelValue" class="logo-upload__empty">
                <span class="material-symbols-outlined">cloud_upload</span>
                <strong>{{ $t('components.authentication.companySetup.logoDropTitle') }}</strong>
                <small>{{ $t('components.authentication.companySetup.logoMaxSize') }}</small>
            </span>

            <span v-else class="logo-upload__preview">
                <img :src="modelValue" :alt="$t('components.authentication.companySetup.logoPreviewAlt')">
            </span>
        </button>

        <input
            ref="fileInput"
            class="logo-upload__input"
            type="file"
            accept="image/*"
            @change="handleInputChange"
        >

        <div v-if="modelValue" class="logo-upload__actions">
            <button type="button" @click="openFileDialog">
                {{ $t('components.authentication.companySetup.replaceLogo') }}
            </button>
            <button type="button" @click="clearLogo">
                {{ $t('components.authentication.companySetup.removeLogo') }}
            </button>
        </div>

        <p v-if="errorMessage" class="logo-upload__error">
            {{ errorMessage }}
        </p>
    </div>
</template>

<style lang="scss" scoped>
.logo-upload {
    display: grid;
    gap: 12px;

    &__dropzone {
        display: grid;
        place-items: center;
        width: 100%;
        min-height: 210px;
        padding: 22px;
        border: 1.5px dashed #cfd3e7;
        border-radius: 16px;
        background: rgb(246 247 249);
        cursor: pointer;
        transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;

        &:hover,
        &--active {
            border-color: #605dff;
            background: #fff;
            box-shadow: 0 10px 28px rgba(96, 93, 255, 0.1);
        }
    }

    &__empty {
        display: grid;
        justify-items: center;
        gap: 8px;
        color: var(--bodyColor);
        text-align: center;

        .material-symbols-outlined {
            display: grid;
            place-items: center;
            width: 54px;
            height: 54px;
            border-radius: 16px;
            background: var(--whiteColor);
            color: #605dff;
            font-size: 30px;
            box-shadow: 0 8px 24px rgba(96, 93, 255, 0.1);
        }

        strong {
            max-width: 260px;
            color: var(--blackColor);
            font-size: 15px;
            line-height: 1.4;
        }

        small {
            font-size: 12px;
        }
    }

    &__preview {
        display: grid;
        place-items: center;
        width: 100%;

        img {
            max-width: 220px;
            max-height: 150px;
            object-fit: contain;
        }
    }

    &__input {
        display: none;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;

        button {
            padding: 8px 12px;
            border: 1px solid #eceef2;
            border-radius: 10px;
            background: var(--whiteColor);
            color: #605dff;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
        }
    }

    &__error {
        margin: 0;
        color: #dc2626;
        font-size: 12px;
        text-align: center;
    }
}

:global(.dark-theme) {
    .logo-upload {
        &__dropzone {
            border-color: var(--borderColor);
            background: #15203c;

            &:hover,
            &--active {
                border-color: #8f8dff;
                background: #15203c;
                box-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
            }
        }

        &__empty {
            .material-symbols-outlined {
                background: #0c1427;
                color: #aeb4ff;
            }
        }

        &__actions {
            button {
                border-color: var(--borderColor);
                background: #15203c;
                color: #aeb4ff;
            }
        }
    }
}
</style>
